/* ──────────────────────────────────────────────────────────────
   sessionContext.tsx · SK Queue session + RBAC seam
   ──────────────────────────────────────────────────────────────
   The signed-in identity, sourced from the SK Platform via the Backbone hub
   (GET /v1/me). Crossed with the tenant's subscribed products it yields the
   accessLevel × entitlements that gate the surfaces. Login is owned by the
   platform (see platformAuthRedirect); here we only read/clear the session.

   accessLevel is derived from the REAL account role (accessFromRole) — a real
   product, not a demo toggle. dev@softknack.com (role 'softknack') → sees Setup;
   every org_admin → Overview + Controls only. No "preview as" switcher.
   ────────────────────────────────────────────────────────────── */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { getCurrentUser, logoutUser } from '../services/auth/auth';
import { redirectToPlatformAuth } from '../navigation/platformAuthRedirect';

/**
 * Access level, derived from the logged-in account's real role (not a demo
 * toggle). `softknack` = platform admin (dev@softknack.com) → sees Setup;
 * `client` = org admin → Overview + Controls; `staff`/`public` = narrower.
 */
export type AccessLevel = 'softknack' | 'client' | 'staff' | 'public';

export interface Entitlements {
  waitlist: boolean;
  voice: boolean;
  chat: boolean;
}

export interface SessionUser { id?: string; email?: string; fullName?: string }
export interface SessionOrg { id?: string; name?: string }

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  fullName: string;
  accessLevel: AccessLevel;
  entitlements: Entitlements;
  /** Re-probe /v1/me (e.g. after an entitlement change) to refresh access/products. */
  refresh: () => Promise<void>;
  /** Clear the session cookies then hand back to the platform login. */
  logout: () => Promise<void>;
}

/** Map a platform org role slug → queue access level (RBAC). */
function accessFromRole(role: string | null): AccessLevel {
  switch (role) {
    case 'platform_admin':
    case 'softknack':
      return 'softknack';
    case 'org_admin':
    case 'admin':
      return 'client';
    case 'staff':
    case 'member':
    case 'user':
      return 'staff';
    default:
      return 'client'; // a signed-in user with an unknown role defaults to client admin
  }
}

/** Derive product entitlements from the platform's subscribed-products list. */
function entitlementsFromProducts(products: string[] | undefined): Entitlements {
  const set = new Set((products ?? []).map((p) => p.toLowerCase()));
  // If the platform doesn't enumerate products yet, default to waitlist on
  // (the core product) and POOL views off until subscribed.
  if (set.size === 0) return { waitlist: true, voice: false, chat: false };
  return {
    waitlist: set.has('queue') || set.has('waitlist'),
    voice: set.has('voice') || set.has('sk-voice'),
    chat: set.has('chat') || set.has('sk-chat'),
  };
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [products, setProducts] = useState<string[] | undefined>(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getCurrentUser();
      const d = res?.data ?? res;
      const u = d?.user ?? null;
      const o = d?.org ?? null;
      setUser(u ? { id: u.id, email: u.email, fullName: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() } : null);
      setOrg(o ? { id: o.id, name: o.name } : null);
      // Role priority: a PLATFORM-SCOPED role (platform_admin, orgId=null) wins
      // over the org membership role — it's how /v1/me flags Softknack staff.
      // Otherwise use the active org's membership role.
      const memberships = Array.isArray(d?.memberships) ? d.memberships : [];
      const activeM = (o?.id && memberships.find((m: any) => m.orgId === o.id)) || memberships[0];
      const platformRoles: string[] = Array.isArray(d?.platform_roles) ? d.platform_roles : [];
      const isPlatformAdmin = d?.is_platform_admin === true || platformRoles.includes('platform_admin');
      setRole(isPlatformAdmin ? 'platform_admin' : (activeM?.role ?? d?.role ?? null));
      // Entitlements: the platform exposes them on /v1/me as `products_entitled`
      // ([{ slug, access, expires_at }]) — granted automatically by the licensing
      // system on purchase. Map to the product slugs the nav gates on. (Legacy
      // fallbacks kept for any older /me shape.)
      const entitled = Array.isArray(d?.products_entitled)
        ? d.products_entitled.map((p: any) => p?.slug).filter(Boolean)
        : (o?.subscribed_products ?? d?.subscribed_products ?? d?.products);
      setProducts(entitled);
      setIsAuthenticated(!!u);
    } catch {
      setUser(null); setOrg(null); setRole(null); setProducts(undefined);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fullName = user?.fullName ?? '';

  // Sign out: clear the platform session cookies, drop local identity so any
  // flash of the shell shows logged-out, then bounce to the centralized platform
  // login (which returns here after re-auth). SK Queue owns no login UI.
  const logout = useCallback(async () => {
    try { await logoutUser(); } catch { /* clear locally regardless */ }
    setUser(null); setOrg(null); setRole(null); setProducts(undefined);
    setIsAuthenticated(false);
    redirectToPlatformAuth();
  }, []);

  // Access level + entitlements derive ONLY from the real session.
  // An anonymous visitor (no session) is 'public' — they can only ever reach the
  // public surfaces (Guest QR, Now Serving); a signed-in user maps by role.
  // Entitlements come from the org's subscribed products.
  const accessLevel = useMemo<AccessLevel>(() => {
    if (!isAuthenticated) return 'public';
    return accessFromRole(role);
  }, [role, isAuthenticated]);
  const entitlements = useMemo<Entitlements>(
    () => entitlementsFromProducts(products),
    [products],
  );

  const value = useMemo<SessionValue>(() => ({
    user, org, role, isAuthenticated, loading, fullName,
    accessLevel, entitlements, refresh, logout,
  }), [user, org, role, isAuthenticated, loading, fullName, accessLevel, entitlements, refresh, logout]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
